import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About Us', href: '/about' },
  { label: 'Service Areas', href: '/service-areas' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  // Transparent = on homepage AND not yet scrolled past 50px
  const transparent = isHome && !scrolled;

  return (
    <>
      {/*
        POSITIONING LOGIC:
        - Homepage: ALWAYS fixed (out of flow so hero extends under it). Background changes on scroll.
        - All other pages: sticky top-0 (in flow, solid white always).
        This prevents the fixed→sticky layout jump that occurs on scroll.
      */}
      <header
        className={`w-full z-50 transition-all duration-400 ${
          isHome
            ? `fixed top-0 left-0 right-0 ${
                scrolled
                  ? 'bg-white border-b border-stone-dark shadow-sm'
                  : ''
              }`
            : 'sticky top-0 bg-white border-b border-stone-dark shadow-sm'
        }`}
      >
        {/* Top utility bar — always inside the wrapper */}
        <div
          className={`text-xs py-2 px-6 hidden md:flex items-center justify-between transition-all duration-400 ${
            transparent
              ? 'bg-black/30 border-b border-white/10 text-white/80'
              : 'bg-charcoal text-white/80 border-b border-white/5'
          }`}
        >
          <div className="max-w-7xl w-full mx-auto flex justify-between items-center">
            <div className="flex items-center gap-6">
              <a
                href="tel:3863014399"
                className="flex items-center gap-1.5 hover:text-gold transition-colors whitespace-nowrap tracking-wide"
              >
                <i className="ri-phone-fill text-gold text-xs"></i>
                (386) 301-4399
              </a>
              <span className="opacity-20">|</span>
              <a
                href="tel:3865668781"
                className="flex items-center gap-1.5 hover:text-gold transition-colors whitespace-nowrap tracking-wide"
              >
                <i className="ri-phone-fill text-gold text-xs"></i>
                (386) 566-8781
              </a>
              <span className="opacity-20">|</span>
              <a
                href="mailto:dippelsoutdoor@gmail.com"
                className="flex items-center gap-1.5 hover:text-gold transition-colors whitespace-nowrap tracking-wide"
              >
                <i className="ri-mail-fill text-gold text-xs"></i>
                dippelsoutdoor@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-4 opacity-60">
              <span className="tracking-wide">Mon–Fri 9AM–3PM &nbsp;·&nbsp; Sat by Appt</span>
              <a
                href="https://www.facebook.com/DippelsOutdoorServices"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full border border-white/20 hover:border-gold hover:text-gold transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-xs"></i>
              </a>
              <a
                href="https://www.google.com/maps/place/Dippel%27s+Outdoor+Services/@28.8555939,-81.0881054,12z"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full border border-white/20 hover:border-gold hover:text-gold transition-colors cursor-pointer"
              >
                <i className="ri-map-pin-2-fill text-xs"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Main nav bar */}
        <nav
          className={`w-full transition-all duration-400 ${
            transparent ? 'bg-transparent' : 'bg-white'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src="https://dippelsoutdoorservices.com/wp-content/uploads/2025/08/logo-303w.webp"
                alt="Dippel's Outdoor Services Logo"
                className="h-11 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-[13px] font-medium tracking-wide transition-colors whitespace-nowrap relative group ${
                    transparent
                      ? 'text-white/85 hover:text-white'
                      : 'text-charcoal-muted hover:text-charcoal'
                  } ${
                    location.pathname === link.href
                      ? transparent ? 'text-white' : 'text-charcoal'
                      : ''
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${
                      location.pathname === link.href ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  ></span>
                </Link>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:3863014399"
                className={`flex items-center gap-2 text-[13px] font-medium px-4 py-2.5 rounded-full transition-all whitespace-nowrap ${
                  transparent
                    ? 'text-white border border-white/30 hover:border-white/60 hover:bg-white/10'
                    : 'text-charcoal border border-stone-dark hover:border-charcoal/40'
                }`}
              >
                <i className="ri-phone-fill text-gold text-xs"></i>
                Call Now
              </a>
              <Link
                to="/contact"
                className={`text-[13px] font-semibold px-6 py-2.5 rounded-full transition-colors whitespace-nowrap tracking-wide ${
                  transparent
                    ? 'bg-gold text-charcoal hover:bg-gold-light'
                    : 'bg-charcoal text-white hover:bg-forest'
                }`}
              >
                Request Consultation
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                transparent ? 'text-white' : 'text-charcoal'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="lg:hidden bg-white border-t border-stone-dark px-6 py-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block py-3.5 text-[13px] font-medium border-b border-stone-dark/60 transition-colors ${
                    location.pathname === link.href
                      ? 'text-forest'
                      : 'text-charcoal hover:text-forest'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-5 flex flex-col gap-3">
                <a
                  href="tel:3863014399"
                  className="flex items-center justify-center gap-2 border border-charcoal/20 text-charcoal rounded-full py-3 text-sm font-semibold whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-gold"></i>
                  (386) 301-4399
                </a>
                <Link
                  to="/contact"
                  className="bg-charcoal text-white text-center py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-forest transition-colors"
                >
                  Request Consultation
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
}
